import { useFormikContext } from "formik";
import {
    object,
    number,
    string,
    boolean,
    NumberSchema,
    BooleanSchema,
    ObjectSchema,
    array
} from "yup";
import { Gender } from "../../common/interfaces/enums/Gender";
import { PaymentFrequency } from "../../common/interfaces/enums/PaymentFrequency";
import ProductType, {
    ClientProductTypesList
} from "../../common/interfaces/enums/ProductType";
import { SmokerStatus } from "../../common/interfaces/enums/SmokerStatus";
import SortBy from "../enums/SortBy";
import { IQuoteClient } from "../interfaces/form/IQuoteClient";
import { IQuoteSettings } from "../interfaces/form/IQuoteSettings";
import { IOccupationQuoteClient } from "../interfaces/form/occupation/IOccupationQuoteClient";
import { IWaiverOfPremiumQuoteClient } from "../interfaces/form/waiverofpremium/IWaiverOfPremiumQuoteClient";
import HealthValidation from "../validation/HealthValidation";
import IncomeValidation from "../validation/IncomeValidation";
import LifeValidation from "../validation/LifeValidation";
import MortgageValidation from "../validation/MortgageValidation";
import OccupationValidation from "../validation/OccupationValidation";
import TraumaValidation from "../validation/TraumaValidation";

export type BaseClientSettingsSchema = {
    isSelected: BooleanSchema;
    personId: NumberSchema;
    isChild: BooleanSchema;
};

const createClientSchema = (
    includeProducts: ProductType[]
): ObjectSchema<IQuoteClient> => {
    // Only include validation for products we care about
    const isIncluded = <T extends object>(
        schema: ObjectSchema<T>,
        productTypes: ProductType[]
    ): ObjectSchema<T> => {
        const isIncluded = includeProducts.some((s) => productTypes.includes(s));

        if (!isIncluded) return null;

        return schema;
    };

    return object<IQuoteClient>({
        // Basic Client details
        age: number().required("Please select an age"),
        isActive: boolean().notRequired().default(false),
        isChild: boolean().notRequired().default(false),
        personId: number().notRequired().default(0),
        gender: string<Gender>()
            .notOneOf([Gender.Unknown], "Please select a gender")
            .required("Please select a gender"),
        smokerStatus: string<SmokerStatus>().when("isChild", {
            is: (val) => !val,
            then: (schema) =>
                schema
                    .notOneOf([SmokerStatus.Unknown], "Please select a smoking status")
                    .required("Please select a smoking status")
        }),

        // This could be better served using concat?
        // https://github.com/jquense/yup#objectconcatschemab-objectschema-objectschema
        [ProductType.Life]: isIncluded(LifeValidation.client, [ProductType.Life]),
        [ProductType.Trauma]: isIncluded(TraumaValidation.client, [ProductType.Trauma]),
        [ProductType.Health]: isIncluded(HealthValidation.client, [ProductType.Health]),
        [ProductType.Income]: isIncluded(IncomeValidation.client, [ProductType.Income]),
        [ProductType.Mortgage]: isIncluded(MortgageValidation.client, [
            ProductType.Mortgage
        ]),
        occupation: object<IOccupationQuoteClient>().when(
            [ProductType.Income, ProductType.Mortgage],
            {
                is: (income, mortgage) => {
                    const isSelectedAndIncluded =
                        (income?.isSelected &&
                            includeProducts.includes(ProductType.Income)) ||
                        (mortgage?.isSelected &&
                            includeProducts.includes(ProductType.Mortgage));

                    return isSelectedAndIncluded;
                },
                then: OccupationValidation.client
            }
        ),

        // TODO: WOP
        [ProductType.WaiverOfPremium]: object<IWaiverOfPremiumQuoteClient>()
            .notRequired()
            .nullable()
            .default(null)
    });
};

const createSchema = (
    includeProducts: ProductType[]
): ObjectSchema<IQuoteSettings, object> => {
    const validateProduct = <T extends object>(
        schema: ObjectSchema<T>,
        product: ProductType
    ): ObjectSchema<T> => {
        if (!includeProducts.includes(product)) return null;
        return schema;
    };

    return object<IQuoteSettings>({
        paymentFrequency: string<PaymentFrequency>().notRequired(),
        sortBy: string<SortBy>().notRequired(),
        [ProductType.Life]: validateProduct(LifeValidation.root, ProductType.Life),
        [ProductType.Mortgage]: validateProduct(
            MortgageValidation.root,
            ProductType.Mortgage
        ),
        clients: array<IQuoteClient>()
            .of(createClientSchema(includeProducts))
            .test(
                "at-least-one-adult-selected",
                "Please select at least one adult",
                (item) => {
                    // If we are not looking at products, then we don't can about selection
                    if (includeProducts.length === 0) return true;

                    // This filters out non-client facing products like WOP
                    const clientProductsToCheck = ClientProductTypesList.filter(
                        (product) => includeProducts.includes(product)
                    );

                    return (
                        item.filter(
                            (c) =>
                                !c.isChild &&
                                clientProductsToCheck
                                    .filter((product) => Object.keys(c).includes(product))
                                    .some((product) => c[product]?.isSelected)
                        ).length > 0
                    );
                }
            )
    });
};

const getFormikError = (field: string): string | boolean => {
    const { submitCount, getFieldMeta } = useFormikContext();

    const { error, touched } = getFieldMeta(field);
    return (touched || submitCount > 0) && error;
};

interface ISettingsValidation {
    createSchema: typeof createSchema;
    getFormikError: typeof getFormikError;
}

const useSettingsValidation = (): ISettingsValidation => {
    return {
        createSchema,
        getFormikError
    };
};

export default useSettingsValidation;
